import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { Plus, Trash2, ArrowRight } from "lucide-react";

export default function AdminLots() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "", description: "", price_per_lot: 100000,
    lock_period_days: 60, expected_sale_months: 5,
  });

  const load = async () => { const r = await api.get("/admin/lots"); setItems(r.data); };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.name) return toast.error("Name required");
    try {
      await api.post("/admin/lots", { ...form, animal_ids: [] });
      toast.success("Lot created");
      setShowForm(false);
      setForm({ name: "", description: "", price_per_lot: 100000, lock_period_days: 60, expected_sale_months: 5 });
      load();
    } catch (_) { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this lot?")) return;
    try { await api.delete(`/admin/lots/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-display text-3xl">Lots</h1>
        <button onClick={() => setShowForm(true)} data-testid="add-lot-btn"
          className="px-4 py-2 rounded-xl bg-[#D71921] text-white text-sm flex items-center gap-2">
          <Plus className="w-4 h-4" /> Create lot
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((l) => (
          <div key={l.id} className="gf-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#6B5F58]">{l.lot_code}</div>
                <div className="font-display text-xl mt-1">{l.name}</div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-xs ${
                l.status === "available" ? "bg-[#FCEDED] text-[#D71921]"
                : l.status === "in_progress" ? "bg-blue-100 text-blue-800"
                : l.status === "sold_out" ? "bg-stone-100 text-stone-700"
                : "bg-amber-100 text-amber-800"}`}>{l.status.replace("_"," ")}</span>
            </div>
            <p className="text-sm text-[#6B5F58] mt-2 line-clamp-2 min-h-[2.5em]">{l.description || "—"}</p>
            <div className="grid grid-cols-3 gap-2 mt-3 text-center">
              <Mini label="Price" value={`₹${l.price_per_lot.toLocaleString()}`} />
              <Mini label="Animals" value={l.animal_count} />
              <Mini label="Lock" value={`${l.lock_period_days}d`} />
            </div>
            <div className="flex justify-between items-center mt-4 pt-3 border-t border-[#E8E0D2]">
              <Link to={`/admin/lots/${l.id}`} className="text-sm text-[#D71921] flex items-center gap-1">
                Manage <ArrowRight className="w-3 h-3" />
              </Link>
              {l.status === "available" && (
                <button onClick={() => remove(l.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full gf-card p-10 text-center text-[#6B5F58]">No lots yet. Create your first one!</div>}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 backdrop-blur-md bg-stone-900/40 grid place-items-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl border border-[#E8E0D2] max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-2xl mb-4">Create lot</div>
            <div className="space-y-3">
              <In label="Name" value={form.name} onChange={(v) => setForm({...form, name: v})} testid="lot-name" />
              <In label="Description" value={form.description} onChange={(v) => setForm({...form, description: v})} />
              <div className="grid grid-cols-3 gap-3">
                <In label="Price (₹)" type="number" value={form.price_per_lot} onChange={(v) => setForm({...form, price_per_lot: +v})} />
                <In label="Lock days" type="number" value={form.lock_period_days} onChange={(v) => setForm({...form, lock_period_days: +v})} />
                <In label="Sale (mo)" type="number" value={form.expected_sale_months} onChange={(v) => setForm({...form, expected_sale_months: +v})} />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={submit} data-testid="save-lot-btn" className="flex-1 px-4 py-2.5 rounded-xl bg-[#D71921] text-white font-medium">Save</button>
              <button onClick={() => setShowForm(false)} className="px-4 py-2.5 rounded-xl border border-[#E8E0D2]">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Mini({ label, value }) {
  return <div className="rounded-lg bg-[#F8F4E9] p-2"><div className="text-[10px] uppercase text-[#6B5F58]">{label}</div><div className="text-sm font-medium">{value}</div></div>;
}
function In({ label, value, onChange, type="text", testid }) {
  return (
    <label className="block">
      <span className="text-xs text-[#6B5F58]">{label}</span>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-xl border border-[#E8E0D2] px-3 py-2.5 outline-none focus:border-[#D71921]" />
    </label>
  );
}
